import { useState, useEffect, useCallback, useMemo } from "react";
import { lawyerService } from "../../services/api";
import { useDebounce } from "../useDebounce";

const EMPTY_FILTERS = {
  search: "",
  // Multi-select (board 1l checkboxes, follow-up pack A2): the country filter
  // holds a LIST of selected jurisdictions; [] = filter off. All other facets
  // stay single-select strings. One selection behaves exactly like the old
  // single string (degenerate case).
  jurisdiction: [],
  specialization: "",
  language: "",
  city: "",
};

const asList = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

// Normalize a jurisdiction filter value to its list form: accepts the new
// array form AND the legacy single string ("" clears), so existing
// setFilter("jurisdiction", "Spain") call sites keep today's behavior.
const asJurisdictionList = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean);
  return v ? [v] : [];
};

// A jurisdiction entry may be an ISO code, a country name, or an object.
const jurisdictionKey = (j) => {
  if (!j) return "";
  if (typeof j === "string") return j;
  return j.code || j.name || j.n || j.country || "";
};

// Specializations: prefer the new plural array; fall back to splitting the
// legacy singular free-text field so old rows still render pills.
const specsOf = (l) => {
  const plural = asList(l.specializations);
  if (plural.length) return plural;
  return (l.specialization || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const jurisdictionsOf = (l) => {
  const js = asList(l.jurisdictions).map(jurisdictionKey).filter(Boolean);
  if (js.length === 0 && l.country) js.push(l.country);
  return js;
};

// Directory data hook: fetches the verified lawyer set once, then derives the
// filter facet options and the filtered result set client-side. Structured
// filtering is done client-side so the directory is fully functional before
// the backend filter params (BE-2) exist; the server param names still live
// centrally in lawyerService, ready for a server-side switch.
const useLawyers = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [allLawyers, setAllLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // D-010: account_ids of lawyers this user has a PENDING request with, so the
  // card/detail can show "Request pending" instead of "Contact". Defensive: if
  // the endpoint isn't there yet, this stays empty and cards behave normally.
  const [pendingLawyerIds, setPendingLawyerIds] = useState(() => new Set());

  const debouncedSearch = useDebounce(filters.search, 300);

  const fetchLawyers = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      // verified-only is the directory default (product rule 5). The backend
      // honours it once BE-2 lands; harmless today.
      const response = await lawyerService.getLawyers({ verified: true }, signal);
      if (response.success) {
        setAllLawyers(Array.isArray(response.data) ? response.data : []);
      } else {
        setError(response.error);
      }
    } catch (err) {
      if (err.name === "CanceledError") return;
      console.error("Error fetching lawyers:", err);
      setError("Failed to fetch lawyers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchLawyers(controller.signal);
    return () => controller.abort();
  }, [fetchLawyers]);

  // Load the user's own outgoing pending requests → set of lawyer account_ids.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const res = await lawyerService.getOutgoingRequests(controller.signal);
      if (res.success) {
        const ids = new Set(
          res.data
            .filter((r) => (r.status || "pending") === "pending")
            .map((r) => r.lawyer_account_id ?? r.lawyer?.account_id)
            .filter((v) => v !== undefined && v !== null)
            .map(String)
        );
        setPendingLawyerIds(ids);
      }
    })().catch(() => {});
    return () => controller.abort();
  }, []);

  const markPending = useCallback((lawyer) => {
    const id = lawyer?.account_id;
    if (id === undefined || id === null) return;
    setPendingLawyerIds((prev) => new Set(prev).add(String(id)));
  }, []);

  const isPending = useCallback(
    (lawyer) => pendingLawyerIds.has(String(lawyer?.account_id)),
    [pendingLawyerIds]
  );

  const facets = useMemo(() => {
    const jurisdictions = new Set();
    const specializations = new Set();
    const languages = new Set();
    const cities = new Set();
    allLawyers.forEach((l) => {
      jurisdictionsOf(l).forEach((j) => jurisdictions.add(j));
      specsOf(l).forEach((s) => specializations.add(s));
      asList(l.languages).forEach((lang) => languages.add(lang));
      if (l.city) cities.add(l.city);
    });
    const sorted = (set) => Array.from(set).sort((a, b) => a.localeCompare(b));
    return {
      jurisdictions: sorted(jurisdictions),
      specializations: sorted(specializations),
      languages: sorted(languages),
      cities: sorted(cities),
    };
  }, [allLawyers]);

  const lawyers = useMemo(() => {
    const q = (debouncedSearch || "").trim().toLowerCase();
    return allLawyers.filter((l) => {
      if (q) {
        const hay = `${l.name || ""} ${l.firm || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.jurisdiction.length > 0) {
        // Multi-select union: the lawyer matches when practicing in ANY of the
        // selected jurisdictions. With exactly one selected this is the exact
        // old single-select check.
        const ownJurisdictions = jurisdictionsOf(l);
        if (!filters.jurisdiction.some((j) => ownJurisdictions.includes(j))) {
          return false;
        }
      }
      if (filters.specialization && !specsOf(l).includes(filters.specialization)) {
        return false;
      }
      if (filters.language && !asList(l.languages).includes(filters.language)) {
        return false;
      }
      if (filters.city && (l.city || "") !== filters.city) {
        return false;
      }
      return true;
    });
  }, [allLawyers, debouncedSearch, filters]);

  const setFilter = useCallback((key, value) => {
    setFilters((f) => ({
      ...f,
      [key]: key === "jurisdiction" ? asJurisdictionList(value) : value,
    }));
  }, []);

  // Toggle one value inside the multi-select jurisdiction list (board 1l
  // checkbox behavior): tap = add, tap again = remove.
  const toggleFilter = useCallback((key, value) => {
    setFilters((f) => {
      const list = asJurisdictionList(f[key]);
      return {
        ...f,
        [key]: list.includes(value)
          ? list.filter((v) => v !== value)
          : [...list, value],
      };
    });
  }, []);

  const clearFilter = useCallback((key) => {
    setFilters((f) => ({ ...f, [key]: key === "jurisdiction" ? [] : "" }));
  }, []);

  const resetFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  // Each selected country counts as its own active filter (one chip per
  // selected country); the single-select facets count 0/1 as before.
  const activeFilterCount =
    filters.jurisdiction.length +
    ["specialization", "language", "city"].filter((k) => filters[k]).length;

  return {
    filters,
    setFilter,
    toggleFilter,
    clearFilter,
    resetFilters,
    activeFilterCount,
    lawyers,
    facets,
    total: allLawyers.length,
    loading,
    error,
    retryFetch: () => fetchLawyers(),
    isPending,
    markPending,
  };
};

export default useLawyers;
